import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/safe-storage';

import { MARKETS, type Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  symbolStore —— ARCHITECTURE §10.2 規則 3：**symbol 改變時絕不 remount。**
 *
 *  先說清楚這個 store **不是**什麼：它不是 symbol 的權威。權威是 URL
 *  （`?symbol=2330`），理由見 lib/asof/use-asof.ts 開頭那段——產品狀態必須
 *  可以貼給別人。兩個權威遲早會不同意，所以這裡只有一個寫入方向：
 *
 *      URL ──(route 的 useEffect)──▶ store ──(useSyncExternalStore)──▶ 各個面板
 *
 *  那這個 store 存在的理由是什麼？兩個，都不是「存 symbol」：
 *
 *  1. **訂閱的粒度。** §10.2 規則 3 逐字：「每個面板獨立訂閱，所以 KPI 卡重畫時
 *     5 年份的 K 線 payload 還在路上，畫面不閃。」把 symbol 從路由 context 往下
 *     傳，會讓整棵子樹隨著搜尋參數改變一起重渲染；圖表元件因此丟掉縮放狀態，
 *     而那正是 §10.4.1 規則 1 拼命要保住的東西。
 *
 *  2. **每個市場各記一個代號。** 使用者在台股看 2330、切到美股看 AAPL、再切回來，
 *     應該回到 2330 而不是回到預設值。這是 `app.user_prefs.last_symbol` 的
 *     前端側對應物（那一欄的型別就是 `{market: symbol}`），登入後兩邊同步。
 * ========================================================================== */

/** 兩個市場的落地預設。所有帶 `?symbol` 的頁面都經由 `useSymbol` 讀同一份，使用者不會在換頁時被換股票。 */
export const DEFAULT_SYMBOL: Record<Market, string> = { tw: '2330', us: 'AAPL' };

export interface SymbolState {
  /** 每個市場最後看的代號。 */
  lastSymbol: Record<Market, string>;
  /** 目前生效的市場（由路由寫入，與 documentElement.dataset.market 同源）。 */
  market: Market;
  /** 由路由把 URL 的值同步進來。**這是唯一的寫入點。** */
  setSymbol: (market: Market, symbol: string) => void;
  /** 伺服器端偏好（`/me/prefs.last_symbol`）回來時整批灌入，不觸發導覽。 */
  hydrateFromServer: (lastSymbol: Partial<Record<Market, string>>) => void;
}

const STORAGE_KEY = 'stock.symbol';

function normalize(symbol: string): string {
  return symbol.trim().toUpperCase();
}

export const useSymbolStore = create<SymbolState>()(
  persist(
    (set) => ({
      lastSymbol: { ...DEFAULT_SYMBOL },
      market: 'tw',
      setSymbol: (market, symbol) => {
        const next = normalize(symbol);
        if (next === '') return;
        set((state) =>
          state.market === market && state.lastSymbol[market] === next
            ? state // 同一個值不製造新的 state 物件，訂閱者就不會被叫醒
            : { market, lastSymbol: { ...state.lastSymbol, [market]: next } },
        );
      },
      hydrateFromServer: (lastSymbol) => {
        set((state) => {
          const merged = { ...state.lastSymbol };
          for (const market of MARKETS) {
            const value = lastSymbol[market];
            if (typeof value === 'string' && value.trim() !== '') merged[market] = normalize(value);
          }
          return { lastSymbol: merged };
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(safeLocalStorage),
      // `market` 不持久化：它由 URL 決定，存下來只會在下一次冷啟動時
      // 與網址打架一瞬間，然後被覆蓋——一個只有在慢速裝置上看得到的閃動。
      partialize: (state) => ({ lastSymbol: state.lastSymbol }),
      version: 1,
    },
  ),
);

/** 面板用這個訂閱，拿到的是字串而不是物件——所以只有代號真的變了才重渲染。 */
export function useSymbol(market: Market): string {
  return useSymbolStore((s) => s.lastSymbol[market] ?? DEFAULT_SYMBOL[market]);
}

/** 決定「沒帶 `?symbol` 時要看哪一檔」。路由在 loader 之前就需要它，所以不是 hook。 */
export function resolveInitialSymbol(market: Market, fromUrl: string | undefined): string {
  if (typeof fromUrl === 'string' && fromUrl.trim() !== '') return normalize(fromUrl);
  const remembered = useSymbolStore.getState().lastSymbol[market];
  return remembered ?? DEFAULT_SYMBOL[market];
}
